import {
  ASK_USER_TOOL_NAME,
  ConfirmationMessageTypeEnum,
  type UserConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import type {
  ProcessedMessage,
  ProcessedPart,
} from '../utils/message-processing';
import type { RenderablePart } from './chat-message-list-types';

type ConfirmationAttachment = {
  item: RenderablePart;
  sourceMessageIndex: number;
};

type ConfirmationIndex = {
  byTargetId: Map<string, ConfirmationAttachment[]>;
  unscoped: ConfirmationAttachment[];
};

type UserConfirmationEntry = {
  confirmation: UserConfirmationMessage;
  confirmationIndex: number;
};

export function isConfirmationAnchorPart(
  processedPart: ProcessedPart,
): boolean {
  const dataMessage = processedPart.dataMessage;

  return (
    processedPart.kind === 'data-message' &&
    (dataMessage?.type ===
      ConfirmationMessageTypeEnum.HITLRequestConfirmation ||
      dataMessage?.type === ConfirmationMessageTypeEnum.GuardrailConfirmation)
  );
}

export function getConfirmationAnchorId(
  processedPart: ProcessedPart,
): string | undefined {
  const dataMessage = processedPart.dataMessage;
  const dataMessageId = dataMessage as { id?: unknown } | undefined;

  if (typeof dataMessageId?.id === 'string') {
    return dataMessageId.id;
  }

  return processedPart.messagePartId;
}

function getUserConfirmationFromMessage(
  processedMessage: ProcessedMessage,
): UserConfirmationMessage | null {
  const metadata = processedMessage.message.metadata;
  const metadataRecord = metadata as { type?: unknown } | undefined;

  if (
    metadata &&
    typeof metadata === 'object' &&
    metadataRecord?.type === ConfirmationMessageTypeEnum.UserConfirmation
  ) {
    return metadata as UserConfirmationMessage;
  }

  return null;
}

export function isExternalToolResultsUserMessage(
  processedMessage: ProcessedMessage,
): boolean {
  const metadata = processedMessage.message.metadata;

  if (!metadata || typeof metadata !== 'object') {
    return false;
  }

  const metadataRecord = metadata as { type?: unknown };

  return (
    metadataRecord.type === ConfirmationMessageTypeEnum.ExternalToolResults
  );
}

export function getUserConfirmationsFromMessage(
  processedMessage: ProcessedMessage,
): UserConfirmationEntry[] {
  const confirmations: UserConfirmationEntry[] = [];
  const metadataConfirmation = getUserConfirmationFromMessage(processedMessage);

  if (metadataConfirmation) {
    confirmations.push({
      confirmation: metadataConfirmation,
      confirmationIndex: confirmations.length,
    });
  }

  for (const part of processedMessage.message.parts) {
    const partRecord = part as { data?: unknown };
    const data = partRecord.data;
    const dataRecord = data as { type?: unknown } | undefined;

    if (
      data &&
      typeof data === 'object' &&
      dataRecord?.type === ConfirmationMessageTypeEnum.UserConfirmation
    ) {
      confirmations.push({
        confirmation: data as UserConfirmationMessage,
        confirmationIndex: confirmations.length,
      });
    }
  }

  return confirmations;
}

function shouldSuppressConfirmationReplyText({
  processedMessage,
  confirmation,
}: {
  processedMessage: ProcessedMessage;
  confirmation: UserConfirmationMessage;
}): boolean {
  return (
    processedMessage.hasVisibleContent &&
    confirmation.toolName !== ASK_USER_TOOL_NAME
  );
}

function toHandledConfirmationDataMessage({
  processedMessage,
  confirmation,
}: {
  processedMessage: ProcessedMessage;
  confirmation: UserConfirmationMessage;
}): UserConfirmationMessage {
  if (
    !shouldSuppressConfirmationReplyText({ processedMessage, confirmation })
  ) {
    return confirmation;
  }

  return {
    ...confirmation,
    message: undefined,
    response: '',
    ...(confirmation.decision
      ? {
          decision:
            confirmation.decision.type === 'reject'
              ? {
                  ...confirmation.decision,
                  message: undefined,
                }
              : confirmation.decision,
        }
      : {}),
    ...(confirmation.decisions
      ? {
          decisions: confirmation.decisions.map((decision) =>
            decision.type === 'reject'
              ? {
                  ...decision,
                  message: undefined,
                }
              : decision,
          ),
        }
      : {}),
  };
}

export function createHandledConfirmationPart({
  processedMessage,
  confirmation,
  confirmationIndex,
}: {
  processedMessage: ProcessedMessage;
  confirmation: UserConfirmationMessage;
  confirmationIndex: number;
}): RenderablePart {
  return {
    processedMessage,
    processedPart: {
      part: processedMessage.message.parts[0],
      key: `${
        processedMessage.message.id ?? processedMessage.index
      }-confirmation-${confirmationIndex}`,
      kind: 'handled-confirmation',
      dataMessage: toHandledConfirmationDataMessage({
        processedMessage,
        confirmation,
      }),
    } as ProcessedPart,
    noAssistantHeader: true,
    autoCollapseToolCalls: true,
  };
}

function getConfirmationAttachmentKey(
  attachment: ConfirmationAttachment,
): string {
  return attachment.item.processedPart.key;
}

export function indexConfirmationAttachments(
  processedMessages: ProcessedMessage[],
): ConfirmationIndex {
  const byTargetId = new Map<string, ConfirmationAttachment[]>();
  const unscoped: ConfirmationAttachment[] = [];

  for (const processedMessage of processedMessages) {
    if (processedMessage.message.role !== 'user') {
      continue;
    }

    for (const confirmationEntry of getUserConfirmationsFromMessage(
      processedMessage,
    )) {
      const { confirmation, confirmationIndex } = confirmationEntry;
      const attachment: ConfirmationAttachment = {
        item: createHandledConfirmationPart({
          processedMessage,
          confirmation,
          confirmationIndex,
        }),
        sourceMessageIndex: processedMessage.index,
      };

      if (!confirmation.subs || confirmation.subs.length === 0) {
        unscoped.push(attachment);
        continue;
      }

      for (const targetId of confirmation.subs) {
        const existingAttachments = byTargetId.get(targetId) ?? [];

        existingAttachments.push(attachment);
        byTargetId.set(targetId, existingAttachments);
      }
    }
  }

  return { byTargetId, unscoped };
}

export function getAttachedConfirmations({
  confirmationIndex,
  anchorId,
  anchorMessageIndex,
  consumedConfirmationKeys,
}: {
  confirmationIndex: ConfirmationIndex;
  anchorId: string | undefined;
  anchorMessageIndex: number;
  consumedConfirmationKeys: Set<string>;
}): RenderablePart[] {
  const candidates = anchorId
    ? (confirmationIndex.byTargetId.get(anchorId) ?? [])
    : [];
  const scopedAttachments = candidates.filter(
    (attachment) =>
      attachment.sourceMessageIndex > anchorMessageIndex &&
      !consumedConfirmationKeys.has(getConfirmationAttachmentKey(attachment)),
  );

  const attachments =
    scopedAttachments.length > 0
      ? scopedAttachments
      : confirmationIndex.unscoped
          .filter(
            (attachment) =>
              attachment.sourceMessageIndex > anchorMessageIndex &&
              !consumedConfirmationKeys.has(
                getConfirmationAttachmentKey(attachment),
              ),
          )
          .slice(0, 1);

  const attachedItems: RenderablePart[] = [];

  for (const attachment of attachments) {
    const attachmentKey = getConfirmationAttachmentKey(attachment);

    if (consumedConfirmationKeys.has(attachmentKey)) {
      continue;
    }

    consumedConfirmationKeys.add(attachmentKey);
    attachedItems.push(attachment.item);
  }

  return attachedItems;
}
